import React, { useEffect, useMemo, useState } from 'react';
import { Slider } from '@mui/material';
import { createPortal } from 'react-dom';
import { CalendarDays, Cpu, DollarSign, Hash, Pencil, Thermometer, Users } from 'lucide-react';
import { usePageHeader } from '../user';
import {
  BudgetInputText,
  BudgetInputValue,
  GlobalSettingsTitle,
  SettingsLabels,
  SettingsNote,
} from './data/constants';
import { FilterDropdown, InputField, MakeToast } from '../../../components';
import { BudgetDetailsProps } from './settings.props';
import { createBudget } from '../user/services';
import { getProviders } from '../llm/services/llm';
import { providersTypes } from '../llm/pages/llm.props';
import {
  getAllPermissionsOfLlms,
  loadAllPermissionsOfLlms,
  getBudget,
  LLM_PERMISSIONS_INVALIDATED_EVENT,
} from '../../../services';
import { getOptimizationModel, postOptimizationModel } from './service';
import Logout from '../../../auth/protected/session-expire-modal';
import RoutingBandsSettings from './components/routing-bands-settings';

// import { PermissionTypes } from '../../../utils/interface-types';
// import { NestedRecord } from '../../../utils/context';

function Settings() {
  const userPermissions = localStorage.getItem('permissions');
  const permissions = userPermissions && JSON.parse(userPermissions);
  const [enableBasicEdit, setEnableBasicEdit] = useState<boolean>(false);
  const [enablePersonalEdit, setEnablePersonalEdit] = useState<boolean>(false);
  const [enablePromptEdit, setEnablePromptEdit] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [formDetails, setFormDetails] = useState<BudgetDetailsProps>({
    budget: undefined,
    gateway_budget: undefined,
    budget_reset_frequency: 'monthly',
    gateway_reset_frequency: 'monthly',
    provider: 0,
    model: 0,
    tokens: 0,
    temperature: 0.5,
  });
  const [temporaryFormDetails, setTemporaryFormDetails] = useState<BudgetDetailsProps>({
    budget: undefined,
    gateway_budget: undefined,
    budget_reset_frequency: '',
    gateway_reset_frequency: '',
    provider: 0,
    model: 0,
    tokens: 0,
    temperature: 0.5,
  });
  const [errorsMsgs, setErrorsMsgs] = useState({
    budget: '',
    gateway_budget: '',
    budget_reset_frequency: '',
    gateway_reset_frequency: '',
    provider: '',
    model: '',
    tokens: '',
    temperature: '',
  });
  const [providers, setProviders] = useState<Array<providersTypes>>([]);
  const [filteredModels, setFilteredModels] = useState<{ id: number; name: string }[]>([]);
  const [logout, setLogout] = useState<boolean>(false);
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);

  const BudgetDesign = [
    {
      label: BudgetInputText[2],
      value: BudgetInputValue[1],
    },
    {
      label: BudgetInputText[3],
      value: BudgetInputValue[2],
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getErrorMessage(err: any): string {
    const defaultMessage = 'Something went wrong!';
    const appBudgetError = err?.response?.data?.data?.app_budget?.response?.message;
    const userBudgetMessage = err?.response?.data?.data?.user_budget?.response?.message;
    let errorMessage = defaultMessage;
    if (err?.response?.data?.data?.message || err?.response?.data?.message) {
      errorMessage = err?.response?.data?.data?.message || err?.response?.data?.message;
      return errorMessage;
    }
    if (err?.response?.data?.data?.app_budget?.status === 400) {
      errorMessage = appBudgetError || defaultMessage;
      return errorMessage;
    }
    if (err.response?.status === 403) {
      errorMessage = err.response.data.message || defaultMessage;
      return errorMessage;
    }
    if (userBudgetMessage) {
      errorMessage = userBudgetMessage;
      return errorMessage;
    }
    return errorMessage;
  }

  useEffect(() => {
    getOptimizationModel()
      .then((res) => {
        const promptData = res?.data?.data;
        const updatePromptDetails = {
          tokens: promptData?.max_tokens,
          temperature: promptData?.temperature,
          model: promptData?.model_id,
          provider: promptData?.provider_id,
        };
        setFormDetails((prevDetails) => ({
          ...prevDetails,
          ...updatePromptDetails,
        }));
        setTemporaryFormDetails((prevDetails) => ({
          ...prevDetails,
          ...updatePromptDetails,
        }));
        setHasChanged(false);
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        MakeToast('error', errorMessage);
      });
  }, [hasChanged]);

  useEffect(() => {
    getBudget()
      .then((res) => {
        const budgetData = res?.data?.data;
        const updatedDetails = {
          budget: budgetData?.budget,
          budget_reset_frequency: budgetData?.frequency,
          gateway_budget: budgetData?.gateway_budget,
          gateway_reset_frequency: budgetData?.gateway_reset_frequency,
        };
        setFormDetails((prevDetails) => ({
          ...prevDetails,
          ...updatedDetails,
        }));
        setTemporaryFormDetails((prevDetails) => ({
          ...prevDetails,
          ...updatedDetails,
        }));
        setHasChanged(false);
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        MakeToast('error', errorMessage);
      });
  }, [hasChanged]);

  useEffect(() => {
    loadAllPermissionsOfLlms();
  }, []);

  useEffect(() => {
    const onInvalidated = () => setPermissionsRefreshKey((k) => k + 1);
    window.addEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, onInvalidated);
    return () => window.removeEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, onInvalidated);
  }, []);

  useEffect(() => {
    localStorage.setItem('activeRoute', '/budget-settings');
    const permissionVal = localStorage.getItem('configurations');
    let filteredArray: providersTypes[] | undefined;
    getProviders()
      .then(async (res) => {
        if (res.status === 200) {
          const allProviders = res?.data?.data ?? [];
          if (permissionVal) {
            await loadAllPermissionsOfLlms();
            const chatPermissions = getAllPermissionsOfLlms();
            if (chatPermissions) {
              const uniqueProviders = chatPermissions.reduce(
                (
                  acc: { provider_name: string; proverID: number }[],
                  current: { provider_name: string; proverID: number },
                ) => {
                  if (
                    !acc.find(
                      (item: { provider_name: string }) =>
                        item.provider_name === current.provider_name,
                    )
                  ) {
                    acc.push(current);
                  }
                  return acc;
                },
                [],
              );
              filteredArray = allProviders.filter((item: { id: number }) =>
                uniqueProviders.some((pro) => item.id === pro.proverID),
              );
            }
          }
          setProviders(filteredArray ?? allProviders);
        } else if (res.status === 401) {
          MakeToast('error', 'something went wrong!');
        }
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        MakeToast('error', errorMessage);
      });
  }, [permissionsRefreshKey]);

  useEffect(() => {
    if (providers) {
      const models = getAllPermissionsOfLlms();
      const providerSpecificModels = models?.filter(
        (model: { proverID: string | number }) => model.proverID === formDetails.provider,
      );
      setFilteredModels(
        providerSpecificModels.map((model: { id: number; name: string }) => ({
          id: model.id,
          name: model.name,
        })),
      );
    }
  }, [providers, formDetails.provider]);

  useEffect(() => {
    if (enablePromptEdit && filteredModels?.length === 0) {
      const defaultProviderId = providers[0]?.id;
      if (!formDetails?.provider) {
        setFormDetails((prev) => ({
          ...prev,
          provider: defaultProviderId,
        }));
      }
      const models = getAllPermissionsOfLlms();
      const providerSpecificModels = models?.filter(
        (model: { proverID: number }) => model.proverID === defaultProviderId,
      );
      setFilteredModels(
        providerSpecificModels.map((model: { id: number; name: string }) => ({
          id: model.id,
          name: model.name,
        })),
      );
    }
  }, [enablePromptEdit]);

  // GATEWAY BUDGET ON CHANGES
  const handleGatewayBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errorMessage = '';
    if (name === 'gateway_budget') {
      const budgetValue = parseFloat(value);
      setFormDetails((prevDetails) => ({
        ...prevDetails,
        gateway_budget: budgetValue,
      }));
    }
    if (value === '' || value === undefined) {
      if (name.includes('gateway_budget')) {
        errorMessage = 'Gateway budget is required!';
      }
    }
    setErrorsMsgs((prevErrors) => ({
      ...prevErrors,
      gateway_budget: errorMessage,
    }));
  };

  const handleGatewayCancelClick = () => {
    setEnablePersonalEdit(false);
    setErrorsMsgs((prev) => ({
      ...prev,
      gateway_budget: '',
      gateway_reset_frequency: '',
    }));
    setFormDetails((prev) => ({
      ...prev,
      gateway_budget: temporaryFormDetails.gateway_budget,
      gateway_reset_frequency: temporaryFormDetails.gateway_reset_frequency,
    }));
  };

  const handleGatewayEditClick = () => {
    setEnablePersonalEdit(true);
  };

  // USERS BUDGET ON CHANGES
  const handleUserBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errorMessage = '';
    if (name === 'users_budget') {
      const budgetValue = parseFloat(value);
      setFormDetails((prevDetails) => ({
        ...prevDetails,
        budget: budgetValue,
      }));
    }
    if (value === '' || value === undefined) {
      if (name.includes('users_budget')) {
        errorMessage = 'User budget is required!';
      }
    }
    setErrorsMsgs((prevErrors) => ({
      ...prevErrors,
      budget: errorMessage,
    }));
  };

  const handleUsersEditClick = () => {
    setEnableBasicEdit(true);
  };

  const handleUsersCancelClick = () => {
    setEnableBasicEdit(false);
    setErrorsMsgs((prev) => ({
      ...prev,
      budget: '',
      budget_reset_frequency: '',
    }));
    setFormDetails((prev) => ({
      ...prev,
      budget: temporaryFormDetails.budget,
      budget_reset_frequency: temporaryFormDetails.budget_reset_frequency,
    }));
  };

  const handleGatewaySaveClick = () => {
    if (errorsMsgs.gateway_budget) {
      MakeToast('error', errorsMsgs.gateway_budget);
      return;
    }
    const gatewayPayload = {
      gateway_budget: formDetails.gateway_budget,
      gateway_reset_frequency: formDetails.gateway_reset_frequency,
      budget: temporaryFormDetails.budget,
      budget_reset_frequency: temporaryFormDetails.budget_reset_frequency,
    };
    createBudget(gatewayPayload)
      .then((res) => {
        if (res.status === 200) {
          setEnablePersonalEdit(false);
          setFormDetails((prev) => ({
            ...prev,
            gateway_budget: formDetails.gateway_budget,
            gateway_reset_frequency: formDetails.gateway_reset_frequency,
          }));
          setTemporaryFormDetails((prev) => ({
            ...prev,
            gateway_budget: formDetails.gateway_budget,
            gateway_reset_frequency: formDetails.gateway_reset_frequency,
          }));
          MakeToast('success', 'Gateway budget updated successfully!');
        } else if (res.status === 401) {
          setLogout(true);
        }
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        MakeToast('error', errorMessage);
      });
  };

  const handleUserSaveClick = () => {
    if (errorsMsgs.budget) {
      MakeToast('error', errorsMsgs.budget);
      return;
    }
    const userPayload = {
      gateway_budget: temporaryFormDetails.gateway_budget,
      gateway_reset_frequency: temporaryFormDetails.gateway_reset_frequency,
      budget: formDetails.budget,
      budget_reset_frequency: formDetails.budget_reset_frequency,
    };
    createBudget(userPayload)
      .then((res) => {
        if (res.status === 200) {
          setEnableBasicEdit(false);
          setFormDetails((prev) => ({
            ...prev,
            budget: formDetails.budget,
            budget_reset_frequency: formDetails.budget_reset_frequency,
          }));
          setTemporaryFormDetails((prev) => ({
            ...prev,
            budget: formDetails.budget,
            budget_reset_frequency: formDetails.budget_reset_frequency,
          }));
          MakeToast('success', 'User budget updated successfully!');
        } else if (res.status === 401) {
          setLogout(true);
        }
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        MakeToast('error', errorMessage);
      });
  };

  // Prompt Optimization Configuration ON CHANGES
  const handlePromptEditClick = () => {
    setEnablePromptEdit(true);
  };

  const handlePromptCancelClick = () => {
    setEnablePromptEdit(false);
    setErrorsMsgs((prev) => ({
      ...prev,
      provider: '',
      model: '',
      tokens: '',
      temperature: '',
    }));
    setFormDetails((prev) => ({
      ...prev,
      tokens: temporaryFormDetails.tokens,
      temperature: temporaryFormDetails.temperature,
      model: temporaryFormDetails.model,
      provider: temporaryFormDetails.provider,
    }));
  };

  const handlePromptSaveClick = () => {
    if (errorsMsgs.tokens) {
      MakeToast('error', errorsMsgs.tokens);
      return;
    }
    const payload = {
      max_tokens: formDetails?.tokens,
      model_id: formDetails?.model,
      temperature: formDetails?.temperature,
      provider_id: formDetails?.provider,
    };
    postOptimizationModel(payload)
      .then((res) => {
        if (res.status === 200) {
          setEnablePromptEdit(false);
          setFormDetails((prev) => ({
            ...prev,
            tokens: formDetails?.tokens,
            model: formDetails?.model,
            temperature: formDetails?.temperature,
            provider: formDetails?.provider,
          }));
          setTemporaryFormDetails((prev) => ({
            ...prev,
            tokens: formDetails?.tokens,
            model: formDetails?.model,
            temperature: formDetails?.temperature,
            provider: formDetails?.provider,
          }));
          MakeToast('success', 'Data updated successfully');
        } else if (res.status === 401) {
          MakeToast('error', res.statusText);
          setLogout(true);
        }
      })
      .catch((err) => {
        const errorMessage = getErrorMessage(err);
        MakeToast('error', errorMessage);
      });
  };

  const handleProviderChange = (value: string) => {
    const providerId = Number(value);
    const models = getAllPermissionsOfLlms();
    const providerSpecificModels = models?.filter(
      (model: { proverID: number }) => model.proverID === providerId,
    );
    setFilteredModels(
      providerSpecificModels.map((model: { id: number; name: string }) => ({
        id: model.id,
        name: model.name,
      })),
    );
    setFormDetails((prev) => ({
      ...prev,
      provider: providerId,
      model: providerSpecificModels[0]?.id ?? prev.model,
    }));
  };

  const handleModelChange = (value: string) => {
    const modelId = Number(value);
    setFormDetails((prev) => ({ ...prev, model: modelId }));
  };

  const handleSliderTemperature = (_event: Event, value: number | number[]) => {
    const temperatureValue = Array.isArray(value) ? value[0] : value;
    setFormDetails((prevDetails) => ({
      ...prevDetails,
      temperature: temperatureValue,
    }));
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errorMessage = '';
    if (name === 'tokens') {
      const tokenValue = parseFloat(value);
      setFormDetails((prevDetails) => ({
        ...prevDetails,
        tokens: tokenValue,
      }));
    }
    if (value === '' || value === undefined) {
      if (name.includes('tokens')) {
        errorMessage = 'Tokens are required!';
      }
    }
    setErrorsMsgs((prevErrors) => ({
      ...prevErrors,
      tokens: errorMessage,
    }));
  };

  const getProviderNameById = (id: string | number | undefined) => {
    if (id == null || id === '') return '';
    const provider = providers?.find((p) => Number(p.id) === Number(id));
    return provider ? provider.provider_name : '';
  };

  const getModelNameById = (id: number | string | undefined) => {
    if (id == null || id === '') return '';
    const model = filteredModels?.find((m) => m.id === Number(id));
    if (model) return model.name;
    const allModels = getAllPermissionsOfLlms();

    const fallback = allModels?.find(
      (m: { id: number | string }) => Number(m.id) === Number(id),
    ) as { name?: string } | undefined;
    return fallback?.name ?? '';
  };

  // let permittedAccess: PermissionTypes[] = [];

  // if (permissions) {
  const hasPermissionForModule = (
    permissionAccess: { module: string; data: string[] }[],
    targetModule: string,
  ): boolean => {
    return permissionAccess.some(
      (perm) =>
        perm.module?.toLowerCase() === targetModule.toLowerCase() && perm.data?.includes('view'),
    );
  };
  // permittedAccess = permissions?.filter(
  //   (permission: { module: string; data: NestedRecord }) =>
  //     permission.module === 'Budget Settings' ||
  //     (permission.module === 'Prompt Optimization Configuration' && permission.data),
  // );

  const showBudgetSettings = hasPermissionForModule(permissions, 'Budget Settings');
  const showPiiConfiguration = hasPermissionForModule(
    permissions,
    'Prompt Optimization Configuration',
  );

  const headerLeftContent = useMemo(
    () => (
      <div className="flex flex-col gap-1">
        <span className="text-2xl font-bold text-[#0A1116]">{GlobalSettingsTitle}</span>
        <span className="text-sm text-[#6A6A6A]">
          Manage budgets and prompt optimization across your organization.
        </span>
      </div>
    ),
    [],
  );

  const frequencyOptions = BudgetDesign.filter(
    (o) => o.value === BudgetInputValue[1] || o.value === BudgetInputValue[2],
  ); // Monthly, Yearly

  usePageHeader({ headerLeftContent });

  return (
    <>
      <div className="max-w-6xl w-full mx-auto flex flex-col gap-6">
        <RoutingBandsSettings />
        {showBudgetSettings && (
          <>
            {/* Gateway Budget Card */}
            <div className="border border-[#E4E4E7]/60 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden bg-white">
              <div className="h-1 bg-gradient-to-r from-[#0A8BC7] to-[#0A8BC7]/60" />
              <div className="p-6">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0A8BC7]/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#0A8BC7]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#101828]">{SettingsLabels[0]}</h2>
                      <p className="text-xs text-[#6A6A6A] mt-0.5">{SettingsNote}</p>
                    </div>
                  </div>
                  {!enablePersonalEdit ? (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-medium text-[#393939] hover:bg-[#0A8BC7] hover:text-white hover:border-[#0A8BC7] transition-colors"
                      onClick={handleGatewayEditClick}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-medium text-[#393939]"
                        onClick={handleGatewayCancelClick}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0A8BC7] text-white text-sm font-medium hover:bg-[#0971a3] transition-colors"
                        onClick={handleGatewaySaveClick}
                      >
                        Save
                      </button>
                    </div>
                  )}
                </div>
                <div className="border-t border-[#E4E4E7] my-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-[#101828]">
                      Budget <span className="text-[#ef4444]">*</span>
                    </span>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A6A6A]" />
                      <InputField
                        type="number"
                        name="gateway_budget"
                        value={formDetails?.gateway_budget}
                        handleChange={handleGatewayBudgetChange}
                        error={errorsMsgs.gateway_budget}
                        editable={enablePersonalEdit}
                        profileInputStyle
                        min={0}
                        className="[&_input]:pl-9 [&_input]:h-11"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-[#101828]">
                      Budget reset frequency <span className="text-[#ef4444]">*</span>
                    </span>
                    <div className="flex items-center gap-3 h-11">
                      {frequencyOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          disabled={!enablePersonalEdit}
                          onClick={() =>
                            enablePersonalEdit &&
                            setFormDetails((prev) => ({
                              ...prev,
                              gateway_reset_frequency: option.value,
                            }))
                          }
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            formDetails?.gateway_reset_frequency?.toLowerCase() === option.value
                              ? 'bg-[#0A8BC7]/10 text-[#0A8BC7] ring-1 ring-[#0A8BC7]/30'
                              : 'bg-[#F4F7FE] text-[#6A6A6A] hover:bg-[#E4E4E7]/50'
                          } ${!enablePersonalEdit ? 'opacity-70 cursor-default' : 'cursor-pointer'}`}
                        >
                          <CalendarDays className="w-3.5 h-3.5" />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Budget Card */}
            <div className="border border-[#E4E4E7]/60 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden bg-white">
              <div className="h-1 bg-gradient-to-r from-[#F48B00] to-[#F48B00]/60" />
              <div className="p-6">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#F48B00]/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#F48B00]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#101828]">{SettingsLabels[1]}</h2>
                      <p className="text-xs text-[#6A6A6A] mt-0.5">
                        Per-user budget allocation and reset configuration.
                      </p>
                    </div>
                  </div>
                  {!enableBasicEdit ? (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-medium text-[#393939] hover:bg-[#F48B00] hover:text-white hover:border-[#F48B00] transition-colors"
                      onClick={handleUsersEditClick}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-medium text-[#393939]"
                        onClick={handleUsersCancelClick}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#F48B00] text-white text-sm font-medium hover:bg-[#e07d00] transition-colors"
                        onClick={handleUserSaveClick}
                      >
                        Save
                      </button>
                    </div>
                  )}
                </div>
                <div className="border-t border-[#E4E4E7] my-4" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-[#101828]">
                      Budget <span className="text-[#ef4444]">*</span>
                    </span>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A6A6A] z-10" />
                      <InputField
                        type="number"
                        name="users_budget"
                        value={formDetails?.budget}
                        handleChange={handleUserBudgetChange}
                        error={errorsMsgs.budget}
                        editable={enableBasicEdit}
                        profileInputStyle
                        min={0}
                        className="[&_input]:pl-9 [&_input]:h-11"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-[#101828]">
                      Budget reset frequency <span className="text-[#ef4444]">*</span>
                    </span>
                    <div className="flex items-center gap-3 h-11">
                      {frequencyOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          disabled={!enableBasicEdit}
                          onClick={() =>
                            enableBasicEdit &&
                            setFormDetails((prev) => ({
                              ...prev,
                              budget_reset_frequency: option.value,
                            }))
                          }
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            formDetails?.budget_reset_frequency?.toLowerCase() === option.value
                              ? 'bg-[#F48B00]/10 text-[#F48B00] ring-1 ring-[#F48B00]/30'
                              : 'bg-[#F4F7FE] text-[#6A6A6A] hover:bg-[#E4E4E7]/50'
                          } ${!enableBasicEdit ? 'opacity-70 cursor-default' : 'cursor-pointer'}`}
                        >
                          <CalendarDays className="w-3.5 h-3.5" />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Prompt Optimization Configuration Card */}
        {showPiiConfiguration && (
          <div className="border border-[#E4E4E7]/60 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden bg-white mb-4">
            <div className="h-1 bg-gradient-to-r from-[#7C3AED] to-[#7C3AED]/60" />
            <div className="p-6">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#7C3AED]/10 flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-[#7C3AED]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#101828]">{SettingsLabels[2]}</h2>
                    <p className="text-xs text-[#6A6A6A] mt-0.5">
                      Fine-tune provider, model, and generation parameters.
                    </p>
                  </div>
                </div>
                {!enablePromptEdit ? (
                  <button
                    type="button"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-medium text-[#393939] hover:bg-[#7C3AED] hover:text-white hover:border-[#7C3AED] transition-colors"
                    onClick={handlePromptEditClick}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm font-medium text-[#393939]"
                      onClick={handlePromptCancelClick}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-medium hover:bg-[#6d28d9] transition-colors"
                      onClick={handlePromptSaveClick}
                    >
                      Save
                    </button>
                  </div>
                )}
              </div>
              <div className="border-t border-[#E4E4E7] my-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-[#101828]">Provider</span>
                  {enablePromptEdit ? (
                    <FilterDropdown
                      value={formDetails?.provider != null ? String(formDetails.provider) : ''}
                      onChange={handleProviderChange}
                      options={
                        providers?.map((opt) => ({
                          value: String(opt.id),
                          label: opt.provider_name,
                        })) ?? []
                      }
                      placeholder="Select Provider"
                      showClear={false}
                      searchable
                      searchPlaceholder="Search providers..."
                    />
                  ) : (
                    <InputField
                      label=""
                      value={getProviderNameById(formDetails?.provider)}
                      name="providers"
                      editable={false}
                      profileInputStyle
                      isRequired={false}
                    />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-[#101828]">Model</span>
                  {enablePromptEdit ? (
                    <FilterDropdown
                      value={formDetails?.model != null ? String(formDetails.model) : ''}
                      onChange={handleModelChange}
                      options={
                        filteredModels?.map((m) => ({ value: String(m.id), label: m.name })) ?? []
                      }
                      placeholder="Select Model"
                      showClear={false}
                      searchable
                      searchPlaceholder="Search models..."
                    />
                  ) : (
                    <InputField
                      label=""
                      value={getModelNameById(formDetails?.model)}
                      name="model"
                      editable={false}
                      profileInputStyle
                      isRequired={false}
                    />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span
                      className="text-sm font-medium text-[#101828] flex items-center gap-2"
                      id="temperature-label"
                    >
                      <Thermometer className="w-3.5 h-3.5 text-[#6A6A6A]" />
                      Temperature
                    </span>
                    <span className="text-xs font-mono bg-[#F4F7FE] px-2 py-0.5 rounded">
                      {Number(formDetails?.temperature ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <Slider
                    aria-labelledby="temperature-label"
                    value={formDetails?.temperature ?? 0.5}
                    onChange={(_, value) =>
                      handleSliderTemperature(_ as Event, Array.isArray(value) ? value[0] : value)
                    }
                    valueLabelDisplay="auto"
                    min={0}
                    max={1}
                    step={0.01}
                    disabled={!enablePromptEdit}
                    sx={{ opacity: enablePromptEdit ? 1 : 0.7 }}
                  />
                  <div className="flex justify-between text-[10px] text-[#6A6A6A]">
                    <span>Precise</span>
                    <span>Creative</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-[#101828] flex items-center gap-2">
                    <Hash className="w-3.5 h-3.5 text-[#6A6A6A]" />
                    Maximum Tokens
                  </span>
                  <InputField
                    type="number"
                    label=""
                    name="tokens"
                    value={formDetails?.tokens}
                    handleChange={handleTokenChange}
                    editable={enablePromptEdit}
                    profileInputStyle
                    isRequired={false}
                    min={0}
                    error={errorsMsgs.tokens}
                  />
                  <p className="text-[10px] text-[#6A6A6A]">Set to 0 for unlimited tokens.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {logout && createPortal(<Logout />, document.body)}
    </>
  );
}

export default Settings;
